/**
 * 3.9 — MEMORY ADMISSION MICRO-FEEDBACK
 *
 * Memory feedback confirms the record changed. It does not claim Arvexis understood more
 * than the evidence supports.
 *
 * The status is driven by a CONFIRMED write outcome on the server — never by an attempted
 * admission. A refused item, a suppressed topic, a failed extraction or a deduplicated
 * write leaves no stamp, so there is nothing here to show. No counters, no badges, no
 * celebration, no interruption: one calm line that dismisses itself.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// How long the line stays before removing itself.
const VISIBLE_MS = 4000;
// An evidence-gated admission is written after the response returns and makes an extraction
// call, so it lands a beat late. We wait this long and no longer — a later result shows
// nothing rather than appearing retroactively.
const WAIT_MS = 9000;
const POLL_MS = 1500;

export const admissionCopy = (kind, lang = 'en') => {
  if (kind === 'forgotten') return lang === 'es' ? 'Recuerdo eliminado' : 'Memory removed';
  if (kind === 'corrected') return lang === 'es' ? 'Corrección guardada' : 'Memory correction saved';
  return lang === 'es' ? 'Memoria actualizada' : 'Memory updated';
};

/**
 * Watches for a confirmed admission without ever announcing one the member has already
 * seen, or one that predates this surface being opened.
 */
export const useMemoryAdmission = () => {
  const cursorRef = useRef(null);
  const pollRef = useRef(null);
  const dismissRef = useRef(null);
  const [admission, setAdmission] = useState(null);

  const read = useCallback(async () => {
    const url = new URL(`${API_URL}/api/companion/memory/last-admission`);
    if (cursorRef.current) url.searchParams.set('cursor', cursorRef.current);
    const response = await fetch(url.toString(), { credentials: 'include' });
    if (!response.ok) return null;
    return response.json();
  }, []);

  const clearTimers = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (dismissRef.current) { clearTimeout(dismissRef.current); dismissRef.current = null; }
  }, []);

  // Prime the cursor silently. Whatever happened before this surface opened is history,
  // not news, and must never surface as a confirmation of something just done.
  useEffect(() => {
    let alive = true;
    read()
      .then((data) => {
        if (alive && data?.stamp_at) cursorRef.current = data.stamp_at;
      })
      .catch(() => {});
    return () => { alive = false; clearTimers(); };
  }, [read, clearTimers]);

  const show = useCallback((data) => {
    cursorRef.current = data.admitted_at || cursorRef.current;
    setAdmission({ kind: data.kind, admittedAt: data.admitted_at, itemId: data.item_id || null });
    if (dismissRef.current) clearTimeout(dismissRef.current);
    dismissRef.current = setTimeout(() => setAdmission(null), VISIBLE_MS);
  }, []);

  /** Check now. Optionally keep checking for the short window an async write needs. */
  const check = useCallback(async ({ wait = false } = {}) => {
    clearTimers();
    const immediate = await read().catch(() => null);
    if (immediate?.admitted) { show(immediate); return; }
    if (immediate?.stamp_at) cursorRef.current = immediate.stamp_at;
    if (!wait) return;

    const deadline = Date.now() + WAIT_MS;
    pollRef.current = setInterval(async () => {
      if (Date.now() > deadline) { clearTimers(); return; }
      const data = await read().catch(() => null);
      if (data?.admitted) { clearTimers(); show(data); }
    }, POLL_MS);
  }, [read, show, clearTimers]);

  return { admission, check };
};

/**
 * The status itself. Deliberately flat: it reports the record, not understanding.
 * `onOpenMemories` is optional — where the member can go and look, the line is tappable.
 */
export const MemoryAdmissionLine = ({ admission, lang = 'en', onOpenMemories, align = 'center' }) => (
  <AnimatePresence>
    {admission && (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 4 }}
        transition={{ duration: 0.25 }}
        className={`flex pb-2 ${align === 'center' ? 'justify-center' : 'justify-start'}`}
        data-testid="memory-admission-status"
      >
        {onOpenMemories ? (
          <button
            type="button"
            onClick={onOpenMemories}
            className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors"
            data-testid="memory-admission-open-memories"
          >
            <Check className="w-3 h-3" />
            <span>{admissionCopy(admission.kind, lang)}</span>
          </button>
        ) : (
          <span className="flex items-center gap-1.5 text-xs text-white/40">
            <Check className="w-3 h-3" />
            <span>{admissionCopy(admission.kind, lang)}</span>
          </span>
        )}
      </motion.div>
    )}
  </AnimatePresence>
);
